import type { ConstraintDiagnostic } from "./economy-types.js";
import { type Micro, microFromString, rational, settleMicroFloor } from "./exact-numbers.js";

export const ZERO = microFromString("0");
export const ONE = microFromString("1");

export function minMicro(left: Micro, right: Micro): Micro {
  return (left < right ? left : right) as Micro;
}

export function add(...values: readonly Micro[]): Micro {
  return values.reduce<bigint>((sum, value) => sum + value, 0n) as Micro;
}

export function subtract(left: Micro, right: Micro): Micro {
  return (left - right) as Micro;
}

export function multiply(value: Micro, factor: string): Micro {
  const decimal = microFromString(factor);
  return ((value * decimal) / 1_000_000n) as Micro;
}

export function portion(value: Micro, numerator: bigint, denominator: bigint): Micro {
  if (denominator <= 0n || numerator < 0n) throw new TypeError("Invalid nonnegative ratio");
  return ((value * numerator) / denominator) as Micro;
}

export function timedRate(ratePerSecond: Micro, elapsedMs: number): Micro {
  return settleMicroFloor(rational(ratePerSecond * BigInt(elapsedMs), 1_000_000n * 1_000n));
}

export function ratioPortion(value: Micro, received: Micro, requested: Micro): Micro {
  return requested === 0n ? ZERO : portion(value, received, requested);
}

export function constraintDiagnostic(
  resource: "Power" | "Metal",
  requested: Micro,
  received: Micro,
): ConstraintDiagnostic {
  const status =
    requested === 0n
      ? "idle"
      : received < requested
        ? resource === "Power"
          ? "power-limited"
          : "metal-limited"
        : "operating";
  return Object.freeze({
    status,
    requested,
    received,
    explanation:
      requested === 0n
        ? `Idle; no ${resource} requested.`
        : received < requested
          ? `${resource} shortage: received less than requested.`
          : `Fully supplied with ${resource}.`,
  });
}
